/**
 * Tabs und Ansichten stehen nicht in der URL — beim Klick auf „Melden“ wird
 * deshalb aus dem DOM gelesen, welcher Tab/welche Ansicht gerade aktiv ist und
 * welches Fenster (WindowManager) oben liegt, z. B.
 * „Fenster: Müller GmbH · Tab: Dokumente“.
 */

const MAX_ZEICHEN = 300;

/** Aktive Elemente der bekannten Tab-Leisten und Umschalter, mit Präfix. */
const AKTIV: Array<{ selektor: string; praefix: "Tab" | "Ansicht" }> = [
  { selektor: ".tabbar button.on", praefix: "Tab" },
  { selektor: ".tp-tabs .tp-tab.on", praefix: "Tab" },
  { selektor: ".dm-tabs button.on", praefix: "Tab" },
  { selektor: ".mset-tabs .mset-tab.active", praefix: "Tab" },
  { selektor: '[role="tablist"] [role="tab"][aria-selected="true"]', praefix: "Tab" },
  { selektor: ".rd-seg button.active", praefix: "Ansicht" },
  // Dashboard-Umschalter (DashboardShared `Segmented`: Kontakt-Vorgänge/Persönliche, Alle/Team/Eigene).
  { selektor: '[role="group"] > button[aria-pressed="true"]', praefix: "Ansicht" },
];

/** Sichtbar = kein Vorfahr ist `hidden`, `display:none` oder `visibility:hidden`. */
function sichtbar(el: Element): boolean {
  const fenster = el.ownerDocument?.defaultView;
  for (let n: Element | null = el; n; n = n.parentElement) {
    if (n.hasAttribute("hidden")) return false;
    const stil = fenster?.getComputedStyle(n);
    if (stil && (stil.display === "none" || stil.visibility === "hidden")) return false;
  }
  return true;
}

const ignoriert = (el: Element) => el.closest("[data-ticket-ignore]") != null;

/** Beschriftung ohne Zähler-Badges (z. B. „Offen 4“ → „Offen“). */
function beschriftung(el: Element): string {
  let text = "";
  el.childNodes.forEach((k) => {
    if (k.nodeType === 3) text += k.textContent ?? "";
    else if (k.nodeType === 1) {
      const e = k as Element;
      const klasse = typeof e.className === "string" ? e.className : "";
      if (/badge/i.test(klasse) || e.getAttribute("aria-hidden") === "true") return;
      text += ` ${beschriftung(e)} `;
    }
  });
  return text.replace(/\s+/g, " ").trim();
}

function oberstesFenster(root: ParentNode): Element | null {
  let top: Element | null = null;
  let topZ = -Infinity;
  root.querySelectorAll("[data-fenster-titel]").forEach((f) => {
    if (!sichtbar(f) || ignoriert(f)) return;
    const z = Number(f.getAttribute("data-fenster-z") ?? 0) || 0;
    if (z >= topZ) { top = f; topZ = z; }
  });
  return top;
}

export function ansichtAusDom(root: ParentNode): string | null {
  const fenster = oberstesFenster(root);

  const treffer: Array<{ el: Element; text: string }> = [];
  const gesehen = new Set<Element>();
  // Dokumentreihenfolge über alle Selektoren hinweg: ein gemeinsames querySelectorAll.
  const alle = root.querySelectorAll(AKTIV.map((a) => a.selektor).join(", "));
  alle.forEach((el) => {
    if (gesehen.has(el) || ignoriert(el) || !sichtbar(el)) return;
    gesehen.add(el);
    const art = AKTIV.find((a) => el.matches(a.selektor))!;
    const name = beschriftung(el);
    if (name) treffer.push({ el, text: `${art.praefix}: ${name}` });
  });

  // Tabs im obersten Fenster zuerst — dort hat der Nutzer zuletzt gearbeitet.
  const sortiert = fenster
    ? [...treffer.filter((t) => fenster.contains(t.el)), ...treffer.filter((t) => !fenster.contains(t.el))]
    : treffer;

  const teile: string[] = [];
  const titel = fenster?.getAttribute("data-fenster-titel")?.trim();
  if (titel) teile.push(`Fenster: ${titel}`);
  for (const t of sortiert) if (!teile.includes(t.text)) teile.push(t.text);
  if (!teile.length) return null;

  let text = "";
  for (const teil of teile) {
    const naechster = text ? `${text} · ${teil}` : teil;
    if (naechster.length > MAX_ZEICHEN) break;
    text = naechster;
  }
  return (text || teile[0].slice(0, MAX_ZEICHEN)) || null;
}
